import { useCallback } from 'react'
import { BASE_API_URL } from '@app/app-constants'

/**
 * Hook to generate shareable links that work across different customer domains.
 *
 * The generated links use the auth login flow with a return_path parameter,
 * allowing users from any customer domain to access the content after authentication.
 *
 * @returns {Object} Object containing getShareLink and copyShareLink functions
 */
export const useShareLink = () => {
  /**
   * Generates a shareable link for the current page that works across customer domains.
   * Uses the auth login flow with return_path to redirect back to the specific page.
   *
   * @param path - The path to share (defaults to current window location pathname)
   * @returns The shareable URL string
   */
  const getShareLink = useCallback((path?: string): string => {
    const returnPath = path || window.location.pathname
    const loginUrl = new URL(`${BASE_API_URL}/auth/login`)
    loginUrl.searchParams.append('return_path', returnPath)
    return loginUrl.toString()
  }, [])

  /**
   * Copies the shareable link to the clipboard.
   *
   * @param path - The path to share (defaults to current window location pathname)
   * @returns Promise that resolves when the copy is complete
   */
  const copyShareLink = useCallback(
    async (path?: string): Promise<void> => {
      const shareLink = getShareLink(path)
      await navigator.clipboard.writeText(shareLink)
    },
    [getShareLink],
  )

  return {
    getShareLink,
    copyShareLink,
  }
}
